<template>
  <page-header title="Data Synchronization">
    <div class="ml-auto flex gap-2">
      <button
        type="button"
        class="btn-primary h-10"
        :disabled="loading.checkEnv"
        @click="checkEnvironment"
      >
        <CloudIcon class="size-5 -ml-1 mr-2" />
        Re-checking environment
      </button>
    </div>
  </page-header>

  <div class="container mx-auto p-6">
    <!-- Environment Status -->
    <div class="mb-6 bg-white p-4 rounded-lg shadow">
      <h3 class="text-lg font-medium text-gray-900 mb-4">
        Environment Status
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          v-for="env in environments"
          :key="env.name"
          class="p-4 border rounded-lg"
          :class="getEnvironmentStatusClass(env)"
        >
          <div class="flex items-center justify-between">
            <h4 class="font-medium">
              {{ env.label }}
            </h4>
            <span
              class="px-2 py-1 text-xs rounded-full"
              :class="env.status === 'connected' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
            >
              {{ env.status === 'connected' ? 'Connected' : 'Disconnected' }}
            </span>
          </div>
          <div class="mt-2 text-sm text-gray-600">
            <div v-if="env.info">
              <p>{{ env.info.environment }}</p>
            </div>
            <div v-else-if="env.error" class="text-red-600">
              Error: {{ env.error }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Entity Selection -->
    <div class="mb-6 bg-white p-4 rounded-lg shadow">
      <h3 class="text-lg font-medium text-gray-900 mb-4">
        Select Data Type to Synchronize
      </h3>
      <div class="flex items-center gap-4">
        <label class="text-sm font-medium text-gray-700">Entity Type:</label>
        <select
          v-model="selectedEntity"
          class="block rounded-md border-gray-300 shadow-sm min-w-48 focus:border-primary-500 focus:ring-primary-500"
        >
          <option value="">
            Select entity type...
          </option>
          <option value="categories">
            Categories
          </option>
          <option value="developers">
            Developers
          </option>
          <option value="publishers">
            Publishers
          </option>
          <option value="plugins">
            Plugins
          </option>
          <option value="programs">
            Programs
          </option>
        </select>
      </div>

      <!-- Warning for Programs -->
      <div v-if="selectedEntity === 'programs'" class="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div class="flex">
          <div class="shrink-0">
            <ExclamationTriangleIcon class="h-5 w-5 text-amber-400" />
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-amber-800">
              Dependency Warning
            </h3>
            <div class="mt-2 text-sm text-amber-700">
              <p>
                Before synchronizing Programs, consider synchronizing their dependencies first:
                <strong>Categories</strong>, <strong>Developers</strong>, <strong>Publishers</strong>, and <strong>Plugins</strong>.
                Programs reference these entities and may lose connections if they don't exist in the target environment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sync Controls -->
    <div v-if="selectedEntity" class="mb-6 bg-white p-4 rounded-lg shadow">
      <h3 class="text-lg font-medium text-gray-900 mb-4">
        Synchronization Controls - {{ getEntityDisplayName(selectedEntity) }}
      </h3>
      <div class="flex flex-wrap gap-4">
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700">Source:</label>
          <select
            v-model="syncConfig.source"
            class="block rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option v-for="env in availableSourceEnvironments" :key="env.value" :value="env.value">
              {{ env.label }}
            </option>
          </select>
        </div>
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700">Target:</label>
          <select
            v-model="syncConfig.target"
            class="block rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option v-for="env in availableTargetEnvironments" :key="env.value" :value="env.value">
              {{ env.label }}
            </option>
          </select>
        </div>
        <div class="flex items-center gap-2">
          <input
            id="dryRun"
            v-model="syncConfig.dryRun"
            type="checkbox"
            class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          >
          <label for="dryRun" class="text-sm font-medium text-gray-700">Dry Run</label>
        </div>
        <button
          type="button"
          class="btn-primary"
          :disabled="loading.sync || syncConfig.source === syncConfig.target"
          @click="compareEnvironments"
        >
          <ArrowsRightLeftIcon class="size-5 -ml-1 mr-2" />
          Compare {{ getEntityDisplayName(selectedEntity) }} {{ syncConfig.source.toUpperCase() }} → {{ syncConfig.target.toUpperCase() }}
        </button>
        <button
          v-if="comparisonResult
            && comparisonResult.summary
            && (comparisonResult.summary.different > 0 || comparisonResult.summary.missing > 0)
            && !comparisonResult.applied
            && !syncConfig.dryRun"
          type="button"
          class="btn-primary"
          :disabled="loading.apply"
          @click="applySynchronization"
        >
          <CheckIcon class="size-5 -ml-1 mr-2" />
          Apply
        </button>
      </div>
    </div>

    <!-- Loading States -->
    <div v-if="loading.compare || loading.sync || loading.apply" class="mb-6">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div class="flex items-center">
          <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3" />
          <span class="text-blue-800">
            <span v-if="loading.checkEnv">Checking environment connections...</span>
            <span v-else-if="loading.compare">Comparing all environments...</span>
            <span v-else-if="loading.sync">Comparing {{ syncConfig.source.toUpperCase() }} → {{ syncConfig.target.toUpperCase() }}...</span>
            <span v-else-if="loading.apply">Applying synchronization changes...</span>
          </span>
        </div>
      </div>
    </div>

    <!-- Comparison Results -->
    <div v-if="comparisonResult && comparisonResult.summary" class="mb-6 bg-white p-4 rounded-lg shadow">
      <h3 class="text-lg font-medium text-gray-900 mb-4">
        {{ getEntityDisplayName(selectedEntity) }} Comparison Results: {{ comparisonResult.source.toUpperCase() }} → {{ comparisonResult.target.toUpperCase() }}
      </h3>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div class="p-3 bg-green-50 border border-green-200 rounded">
          <div class="text-green-800 font-medium">
            {{ comparisonResult.summary.identical }} Identical
          </div>
        </div>
        <div class="p-3 bg-yellow-50 border border-yellow-200 rounded">
          <div class="text-yellow-800 font-medium">
            {{ comparisonResult.summary.different }} Different
          </div>
        </div>
        <div class="p-3 bg-red-50 border border-red-200 rounded">
          <div class="text-red-800 font-medium">
            {{ comparisonResult.summary.missing }} Missing
          </div>
        </div>
      </div>

      <!-- Sync Results -->
      <div v-if="comparisonResult.applied" class="mb-4">
        <h4 class="text-md font-medium text-gray-900 mb-3">
          Synchronization Results
        </h4>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="p-3 bg-blue-50 border border-blue-200 rounded">
            <div class="text-blue-800 font-medium">
              {{ comparisonResult.applied.created }} Created
            </div>
          </div>
          <div class="p-3 bg-green-50 border border-green-200 rounded">
            <div class="text-green-800 font-medium">
              {{ comparisonResult.applied.updated }} Updated
            </div>
          </div>
          <div class="p-3 bg-gray-50 border border-gray-200 rounded">
            <div class="text-gray-800 font-medium">
              {{ comparisonResult.applied.deleted || 0 }} Deleted
            </div>
          </div>
        </div>
        <div v-if="comparisonResult.applied.errors && comparisonResult.applied.errors.length > 0" class="mt-3">
          <div class="p-3 bg-red-50 border border-red-200 rounded">
            <div class="text-red-800 font-medium">
              {{ comparisonResult.applied.errors.length }} Errors
            </div>
          </div>
        </div>
      </div>

      <!-- Warnings -->
      <div v-if="comparisonResult.warnings && comparisonResult.warnings.length > 0" class="mb-4">
        <h4 class="text-md font-medium text-gray-900 mb-3">
          ⚠️ Warnings
        </h4>
        <div class="space-y-2">
          <div
            v-for="warning in comparisonResult.warnings"
            :key="warning"
            class="p-3 bg-orange-50 border border-orange-200 rounded-lg"
          >
            <div class="flex items-start">
              <div class="shrink-0">
                <svg class="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <div class="text-sm text-orange-800">
                  {{ warning }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Differences Table -->
      <div v-if="comparisonResult.differences && comparisonResult.differences.length > 0" class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Program
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Source
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Target
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Differences
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="diff in comparisonResult.differences"
              :key="diff.id || diff.name"
              :class="getDifferenceRowClass(diff.status)"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="text-sm font-medium text-gray-900">
                    {{ diff.name }}
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                  :class="getDifferenceStatusClass(diff.status)"
                >
                  {{ diff.status }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm text-gray-900">
                <div v-if="diff.sourceVersion">
                  {{ diff.sourceVersion }}
                </div>
                <div v-else class="text-gray-400">
                  -
                </div>
              </td>
              <td class="px-6 py-4 text-sm text-gray-900">
                <div v-if="diff.targetVersion">
                  {{ diff.targetVersion }}
                </div>
                <div v-else class="text-gray-400">
                  -
                </div>
              </td>
              <td class="px-6 py-4 text-sm text-gray-500">
                <div v-if="diff.fields && diff.fields.length > 0" class="space-y-1">
                  <div
                    v-for="field in diff.fields.slice(0, 3)"
                    :key="field"
                    class="text-xs bg-gray-100 px-2 py-1 rounded"
                  >
                    {{ field }}
                  </div>
                  <div v-if="diff.fields.length > 3" class="text-xs text-gray-400">
                    +{{ diff.fields.length - 3 }} more
                  </div>
                </div>
                <div v-else>
                  -
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="text-center py-8 text-gray-500">
        All {{ getEntityDisplayName(selectedEntity).toLowerCase() }} are synchronized
      </div>
    </div>

    <!-- All Environments Comparison -->
    <div v-if="allComparison" class="mb-6 bg-white p-4 rounded-lg shadow">
      <h3 class="text-lg font-medium text-gray-900 mb-4">
        All Environments Comparison
      </h3>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Comparison
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Identical
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Different
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Missing
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="comp in allComparison" :key="`${comp.source}-${comp.target}`">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ comp.source.toUpperCase() }} → {{ comp.target.toUpperCase() }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                  :class="comp.summary.different + comp.summary.missing > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'"
                >
                  {{ comp.summary.different + comp.summary.missing > 0 ? 'Out of Sync' : 'In Sync' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ comp.summary.identical }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ comp.summary.different }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ comp.summary.missing }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                  class="text-indigo-600 hover:text-indigo-900"
                  @click="viewComparison(comp.source, comp.target)"
                >
                  View Details
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useFetch } from '@vueuse/core';
import PageHeader from '../components/PageHeader.vue';
import {
  CloudIcon,
  ArrowsRightLeftIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline';
import { apiDomain } from '../composables/useConstants.js';

const toast = useToast();

// State
const loading = reactive({
  checkEnv: false,
  compare: false,
  sync: false,
  apply: false
});

const environments = ref([
  // { name: 'test', label: 'Environment', status: 'unknown', info: null, error: null }, -- for local testing
  { name: 'bar', label: 'Environment', status: 'unknown', info: null, error: null },
  { name: 'dev', label: 'Environment', status: 'unknown', info: null, error: null },
  { name: 'pro', label: 'Environment', status: 'unknown', info: null, error: null },
  { name: 'beta', label: 'Environment', status: 'unknown', info: null, error: null }
]);

const syncConfig = reactive({
  source: 'bar',
  target: 'dev',
  dryRun: true
});

const selectedEntity = ref('');

// Watch for entity changes to reset results
watch(selectedEntity, () => {
  comparisonResult.value = null;
  allComparison.value = null;
});

// Computed properties for filtering environment options
const allEnvironmentOptions = [
  // { value: 'test', label: 'TEST' }, -- for local testing
  { value: 'bar', label: 'BAR' },
  { value: 'dev', label: 'DEV' },
  { value: 'pro', label: 'PRO' },
  { value: 'beta', label: 'BETA' }
];

const availableSourceEnvironments = computed(() => {
  return allEnvironmentOptions.filter(env => env.value !== syncConfig.target);
});

const availableTargetEnvironments = computed(() => {
  return allEnvironmentOptions.filter(env => env.value !== syncConfig.source);
});

const comparisonResult = ref(null);
const allComparison = ref(null);

async function checkEnvironment() {
  loading.checkEnv = true;

  try {
    // Check all environments using the same POST endpoint
    const envNames = [ 'bar', 'dev', 'pro', 'beta' ]; // 'test' can be added for local testing

    for (const envName of envNames) {
      const env = environments.value.find(e => e.name === envName);
      if (!env) continue;

      try {
        // Get environment info via sync API
        const { data } = await useFetch(`${apiDomain}/api/sync/environment/info`)
          .post({ environment: envName })
          .json();

        if (data.value?.success) {
          env.status = 'connected';
          env.info = data.value;
          env.error = null;
        } else if (data.value?.success === false) {
          // Handle graceful failure from API
          env.status = 'disconnected';
          env.info = null;
          env.error = data.value.message || data.value.error || 'Environment unavailable';
        } else {
          throw new Error(data.value?.message || 'Unknown error');
        }
      } catch (error) {
        env.status = 'disconnected';
        env.info = null;
        env.error = `Connection failed: ${error.message || error}`;
      }
    }

    toast.success('Environment check completed');
  } catch (error) {
    toast.error(`Failed to check environments: ${error.message}`);
  } finally {
    loading.checkEnv = false;
  }
}

async function compareEnvironments() {
  if (syncConfig.source === syncConfig.target) {
    toast.error('Source and target environments cannot be the same');
    return;
  }

  if (!selectedEntity.value) {
    toast.error('Please select an entity type to synchronize');
    return;
  }

  loading.sync = true;

  try {
    const { data } = await useFetch(`${apiDomain}/api/sync/${selectedEntity.value}/compare`)
      .post({
        source: syncConfig.source,
        target: syncConfig.target,
        dryRun: syncConfig.dryRun
      })
      .json();

    if (data.value) {
      comparisonResult.value = data.value;
      toast.success('Comparison completed');
    } else {
      throw new Error('No data received from API');
    }
  } catch (error) {
    toast.error(`Failed to compare environments: ${error.message}`);
  } finally {
    loading.sync = false;
  }
}

async function applySynchronization() {
  if (!comparisonResult.value) return;

  loading.apply = true;

  try {
    const { data } = await useFetch(`${apiDomain}/api/sync/${selectedEntity.value}/sync`)
      .post({
        source: syncConfig.source,
        target: syncConfig.target,
        dryRun: syncConfig.dryRun
      })
      .json();

    if (data.value) {
      // All sync APIs now return SyncResult with 'applied' field
      if (data.value.applied) {
        const applied = data.value.applied;
        toast.success(`Sync completed: ${applied.created || 0} created, ${applied.updated || 0} updated, ${applied.deleted || 0} deleted`);

        if (applied.errors && applied.errors.length > 0) {
          toast.error(`Sync completed with ${applied.errors.length} errors`);
        }

        // Update the comparison result with the sync result
        comparisonResult.value = data.value;
      } else {
        // It's a comparison result
        comparisonResult.value = data.value;
      }
    } else {
      throw new Error('No data received from API');
    }
  } catch (error) {
    toast.error(`Failed to apply synchronization: ${error.message}`);
  } finally {
    loading.apply = false;
  }
}

function viewComparison(source, target) {
  syncConfig.source = source;
  syncConfig.target = target;
  compareEnvironments();
}

// Utility functions
function getEnvironmentStatusClass(env) {
  if (env.status === 'connected') {
    return 'border-green-300 bg-green-50';
  } else if (env.status === 'disconnected') {
    return 'border-red-300 bg-red-50';
  }
  return 'border-gray-300 bg-gray-50';
}

function getDifferenceRowClass(status) {
  if (status === 'missing') return 'bg-red-50';
  if (status === 'different') return 'bg-yellow-50';
  return '';
}

function getDifferenceStatusClass(status) {
  if (status === 'missing') return 'bg-red-100 text-red-800';
  if (status === 'different') return 'bg-yellow-100 text-yellow-800';
  if (status === 'identical') return 'bg-green-100 text-green-800';
  return 'bg-gray-100 text-gray-800';
}

function getEntityDisplayName(entity) {
  const entityNames = {
    programs: 'Programs',
    plugins: 'Plugins',
    categories: 'Categories',
    developers: 'Developers',
    publishers: 'Publishers'
  };
  return entityNames[entity] || entity;
}

// Initialize
onMounted(() => {
  checkEnvironment();
});
</script>
